import { MonoTypeOperatorFunction } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AppInsights } from 'applicationinsights-js';
import { environment } from 'src/environments/environment';

/**
 * RxJS operator that logs values passing through the stream.
 * Logs in all environments (use devLog for development-only logging).
 */
export function tapLog<T>(...args: any): MonoTypeOperatorFunction<T> {
  return input => input.pipe(tap(value => console.log('TapLog: ', value, ...args)));
}

/**
 * RxJS operator that logs values only in development environment.
 * Production-safe logging operator.
 * @param message Optional message or function that returns the log message
 * @param args Additional arguments to pass to console.log
 */
export function devLog<T>(message?: string | ((value: T) => any), ...args: any): MonoTypeOperatorFunction<T> {
  return input =>
    input.pipe(
      tap(value => {
        if (!environment.production) {
          if (typeof message === 'function') {
            console.log(message(value), ...args);
          } else if (message) {
            console.log(message, value, ...args);
          } else {
            console.log(value, ...args);
          }
        }
      })
    );
}

/**
 * Logs an error to the console in development and tracks it to Application
 * Insights in all environments. Plain function (not an operator) for
 * non-stream contexts such as catchError callbacks and event handlers.
 *
 * Handled errors never reach the global AppInsightServiceErrorHandler, so this
 * is the telemetry path for caught failures.
 * @param args Arguments as for console.error; the first Error among them (or
 *             one synthesized from the joined message) is sent to App Insights.
 */
export function logError(...args: any): void {
  if (!environment.production) {
    console.error(...args);
  }
  try {
    const context = args.map((a: any) => (typeof a === 'string' ? a : a instanceof Error ? a.message : a?.message || String(a))).join(' ');
    const exception = args.find((a: any) => a instanceof Error) ?? new Error(context);
    AppInsights.trackException(exception, undefined, { context });
  } catch {
    // App Insights unavailable — never break the caller over telemetry.
  }
}

/**
 * RxJS operator that logs warnings only in development environment.
 * Production-safe warning operator.
 * @param message Warning message or function that returns the warning message
 * @param args Additional arguments to pass to console.warn
 */
export function devWarn<T>(message: string | ((value: T) => any), ...args: any): MonoTypeOperatorFunction<T> {
  return input =>
    input.pipe(
      tap(value => {
        if (!environment.production) {
          if (typeof message === 'function') {
            console.warn(message(value), ...args);
          } else {
            console.warn(message, value, ...args);
          }
        }
      })
    );
}
